// src/encounter/lib/allied-health-plan.ts
// PROD-2040 — the CONTENT of the allied-health plan step, as pure data.
//
// THE RULING THIS ENCODES. "Allied Health" is ONE clinical access level (`TenantMasterId = 20`).
// The discipline a person practises — health coach, dietitian, exercise physiologist — is a
// SPECIALITY, not a role: it changes what they document, never what the app lets them do. So this
// module is a SPECIALITY-DRIVEN VARIATION WITHIN ONE STEP, not three steps and not three roles.
// One step key (`ALLIED_PLAN_STEP`), one role (`'allied-health'`), one view; the speciality
// selects which recognised professional framework the plan is structured on.
//
// TERMINATES IN A PLAN, NOT AN ORDER. Allied health does not prescribe. There is no order type, no
// prescribe field, and no affordance that could become one, anywhere in this module. That is
// enforced mechanically rather than by convention: every field id/label/hint is checked against
// `ORDER_AFFORDANCE_PATTERNS` by `hasOrderAffordance`, which the tests run over the spine and over
// EVERY framework in the registry (and over a deliberately poisoned synthetic input, so the check
// is proven able to fail rather than merely proven to pass).
//
// THE SPINE IS STRUCTURALLY ENFORCED, NOT A CONVENTION (review response, PROD-2040). Every plan is
// an `AlliedPlan`, which carries a REQUIRED `spine` field typed `AlliedPlanSpineSection` — a type
// no framework block satisfies, and which is not assignable into `frameworks`. So:
//   • a resolver path that returns frameworks without a spine does not compile;
//   • a framework block cannot be passed AS the spine, nor the spine as a framework;
//   • `FRAMEWORK_SECTIONS` is an exhaustive `Record<AlliedPlanFramework, …>`, so adding a fourth
//     framework to the union without giving it a section does not compile either.
// `'plan-core'` is deliberately NOT a member of `AlliedPlanFramework`; collapsing the two into one
// flat union is exactly what made the spine unenforceable before.
//
// The frameworks are the recognised professional ones, not invented vocabulary:
//   • FITT-VP  — Frequency, Intensity, Time, Type, Volume, Progression (exercise programming).
//   • PES + ADIME — the Problem/Etiology/Signs-and-symptoms statement and the
//     Assessment/Diagnosis/Intervention/Monitoring/Evaluation process (nutrition care process).
//   • Goals · barriers · behavioural targets (health-behaviour coaching).
//
// Pure: no React, no fetch, no DS import. The view renders whatever this returns.

/** The allied-health disciplines. SPECIALITY values, NOT roles — see the header. */
export type AlliedSpeciality = 'health-coach' | 'dietitian' | 'exercise-physiologist';

/**
 * The three recognised professional frameworks.
 *
 * The SPINE IS NOT A MEMBER, on purpose. It used to be (`'plan-core'` sat in this union next to the
 * three frameworks), and that is precisely why the spine could not be enforced: a flat union makes
 * the spine indistinguishable from a framework at the type level, so nothing stopped a resolver
 * from returning frameworks alone. Keeping them separate is what buys the guarantees in the header.
 */
export type AlliedPlanFramework = 'goals-barriers' | 'pes-adime' | 'fitt-vp';

/** The spine's discriminant. One literal, distinct from every framework. */
export type AlliedPlanSpine = 'plan-core';

/** One authored field of the plan. `multiline` picks the DS control the view mounts. */
export interface AlliedPlanField {
  /** Stable id — the response key, and the React key the view renders each field under. */
  id: string;
  label: string;
  /** Short helper text; the framework's own definition of the field, where it has one. */
  hint?: string;
  multiline?: boolean;
}

/** Shape shared by the spine and every framework block. */
export interface AlliedPlanSectionBase {
  id: string;
  title: string;
  /** One line naming the framework, so the clinician can see which one they are in. */
  subtitle: string;
  fields: AlliedPlanField[];
}

/** The shared spine. `framework` is the `'plan-core'` literal and nothing else. */
export interface AlliedPlanSpineSection extends AlliedPlanSectionBase {
  framework: AlliedPlanSpine;
}

/** A framework block. `framework` can NEVER be `'plan-core'`. */
export interface AlliedPlanFrameworkSection extends AlliedPlanSectionBase {
  framework: AlliedPlanFramework;
}

/** Either kind of section — what the view iterates when it renders fields. */
export type AlliedPlanSection = AlliedPlanSpineSection | AlliedPlanFrameworkSection;

/**
 * A plan: the shared spine plus the framework block(s).
 *
 * `spine` is REQUIRED and separately typed. That is the enforcement — see the module header. There
 * is no way to construct an `AlliedPlan` that skips the spine, and no way to smuggle the spine in
 * as a framework or a framework in as the spine.
 */
export interface AlliedPlan {
  spine: AlliedPlanSpineSection;
  frameworks: AlliedPlanFrameworkSection[];
}

/**
 * What the step terminates in. A PLAN — never an order, never a prescription.
 * Exported so the view and the tests both name the same thing rather than each asserting a string.
 */
export const ALLIED_PLAN_TERMINAL = 'plan' as const;

/**
 * The vocabulary that would make a field an ORDER/PRESCRIBE affordance. Allied health has no
 * prescribing scope, so a field matching any of these is a defect, not a feature — see
 * `hasOrderAffordance`.
 *
 * WORD-BOUNDARY anchored, deliberately. A naive substring list is worse than no check: `sig`
 * matches "Signs and symptoms" and `order` matches "recorded"/"borderline", so a substring check
 * fires on legitimate clinical wording and gets deleted the first time it does. `prescri` is left
 * as a prefix because it must catch prescribe · prescription · prescribing, all of which are the
 * thing being forbidden.
 */
export const ORDER_AFFORDANCE_PATTERNS: readonly RegExp[] = [
  /\borders?\b/i,
  /\bprescri\w*/i,
  /\brx\b/i,
  /\bdispense\w*/i,
  /\brefills?\b/i,
  /\bsigs?\b/i,
  /\bdos(?:e|es|age)\b/i,
  /\bmedications?\b/i,
  /\bdrugs?\b/i,
];

/**
 * The plan's shared spine — present in EVERY plan. This is what makes one step serve three
 * disciplines: the framework block below it varies, this does not.
 *
 * `plan-review-interval` is the plan's own review date, not a scheduling action: it records when
 * the plan is to be revisited. It places no appointment and sends nothing.
 */
const PLAN_CORE: AlliedPlanSpineSection = {
  id: 'allied-plan-core',
  framework: 'plan-core',
  // ONE TITLE (#609 rule, applied with the nurse's card after the design desk's 5 Oct review): in
  // frame v2 this card sits in the P section "Plan", inside the "Plan" block, so it names the plan's
  // own state instead of saying "Plan" a third time.
  title: "Today's plan",
  // Plain words (design desk, 5 Oct): "spine" is an internal word.
  subtitle: 'Recorded for every allied-health discipline.',
  fields: [
    {
      id: 'plan-summary',
      label: 'Plan summary',
      hint: 'What the patient and the clinician agreed, in the clinician’s own words.',
      multiline: true,
    },
    {
      id: 'plan-patient-agreement',
      label: 'Agreed with the patient',
      hint: 'What the patient said they will do, in their words where possible.',
      multiline: true,
    },
    {
      id: 'plan-review-interval',
      label: 'Review interval',
      hint: 'When this plan is to be revisited.',
    },
  ],
};

/** Health-behaviour coaching: goals, barriers, behavioural targets. */
const GOALS_BARRIERS: AlliedPlanFrameworkSection = {
  id: 'allied-plan-goals-barriers',
  framework: 'goals-barriers',
  title: 'Goals, barriers and behavioural targets',
  subtitle: 'Health-behaviour coaching framework.',
  fields: [
    { id: 'goals-primary', label: 'Goal', hint: 'Specific, measurable, and set with the patient.', multiline: true },
    { id: 'goals-confidence', label: 'Confidence to start', hint: 'The patient’s own rating, 0–10.' },
    { id: 'goals-barriers', label: 'Barriers', hint: 'What stands in the way, named by the patient.', multiline: true },
    { id: 'goals-enablers', label: 'Enablers and supports', hint: 'What is already working, and who helps.', multiline: true },
    { id: 'goals-behavioural-target', label: 'Behavioural target', hint: 'The one behaviour to change before the review.', multiline: true },
    { id: 'goals-self-monitoring', label: 'Self-monitoring', hint: 'What the patient will track, and how.', multiline: true },
  ],
};

/**
 * Nutrition care process: the PES statement (Problem · Etiology · Signs and symptoms) followed by
 * the ADIME record (Assessment · Diagnosis · Intervention · Monitoring · Evaluation).
 *
 * "Nutrition diagnosis" is a DIETETIC diagnosis in the nutrition care process — it is not a medical
 * diagnosis, carries no ICD coding, and attaches no orders. It is deliberately NOT wired to the
 * diagnosis search or the A/P canvas for exactly that reason.
 */
const PES_ADIME: AlliedPlanFrameworkSection = {
  id: 'allied-plan-pes-adime',
  framework: 'pes-adime',
  title: 'Nutrition care process',
  subtitle: 'PES statement and ADIME record.',
  fields: [
    { id: 'pes-problem', label: 'P — Problem', hint: 'The nutrition problem (dietetic, not medical).', multiline: true },
    { id: 'pes-etiology', label: 'E — Etiology', hint: 'The root cause the intervention targets.', multiline: true },
    { id: 'pes-signs-symptoms', label: 'S — Signs and symptoms', hint: 'The evidence the problem exists.', multiline: true },
    { id: 'adime-assessment', label: 'A — Assessment', hint: 'Intake, anthropometry, biochemical and client history.', multiline: true },
    { id: 'adime-diagnosis', label: 'D — Nutrition diagnosis', hint: 'Restated from the PES statement above.', multiline: true },
    { id: 'adime-intervention', label: 'I — Intervention', hint: 'Food and nutrient delivery, education, counselling.', multiline: true },
    { id: 'adime-monitoring', label: 'M — Monitoring', hint: 'The indicators to be re-measured.', multiline: true },
    { id: 'adime-evaluation', label: 'E — Evaluation', hint: 'Progress against the indicators since last review.', multiline: true },
  ],
};

/** Exercise programming: FITT-VP. Deliberately not called a prescription — allied health does
 *  not prescribe; this is a plan the patient agrees to, and it attaches no order. */
const FITT_VP: AlliedPlanFrameworkSection = {
  id: 'allied-plan-fitt-vp',
  framework: 'fitt-vp',
  title: 'Exercise plan',
  subtitle: 'FITT-VP framework.',
  fields: [
    { id: 'fitt-frequency', label: 'F — Frequency', hint: 'Sessions per week.' },
    { id: 'fitt-intensity', label: 'I — Intensity', hint: 'RPE, %HRmax, or load.' },
    { id: 'fitt-time', label: 'T — Time', hint: 'Minutes per session.' },
    { id: 'fitt-type', label: 'T — Type', hint: 'Aerobic, resistance, flexibility, neuromotor.', multiline: true },
    { id: 'fitt-volume', label: 'V — Volume', hint: 'Total weekly work — minutes, steps, or MET-minutes.' },
    { id: 'fitt-progression', label: 'P — Progression', hint: 'How the plan advances between reviews.', multiline: true },
    { id: 'fitt-precautions', label: 'Precautions', hint: 'Contraindications and stop rules for this patient.', multiline: true },
  ],
};

/**
 * The framework registry. EXHAUSTIVE over `AlliedPlanFramework`, which is half the spine
 * enforcement: a fourth framework added to the union without a section here is a compile error,
 * so a new framework cannot be introduced through a path that bypasses `alliedPlanFor` (and
 * therefore the spine).
 */
const FRAMEWORK_SECTIONS: Record<AlliedPlanFramework, AlliedPlanFrameworkSection> = {
  'goals-barriers': GOALS_BARRIERS,
  'pes-adime': PES_ADIME,
  'fitt-vp': FITT_VP,
};

/** Which framework each discipline's plan is structured on. Exhaustive over `AlliedSpeciality`. */
const FRAMEWORK_FOR_SPECIALITY: Record<AlliedSpeciality, AlliedPlanFramework> = {
  'health-coach': 'goals-barriers',
  dietitian: 'pes-adime',
  'exercise-physiologist': 'fitt-vp',
};

/** The frameworks a clinician may pick between, in a stable order, with their display labels. */
export const FRAMEWORK_CHOICES: readonly { framework: AlliedPlanFramework; label: string }[] = [
  { framework: 'goals-barriers', label: 'Goals, barriers and behavioural targets' },
  { framework: 'pes-adime', label: 'Nutrition care process (PES and ADIME)' },
  { framework: 'fitt-vp', label: 'Exercise plan (FITT-VP)' },
];

/**
 * Why the step is asking the clinician which framework applies.
 *
 * TWO DISTINCT REASONS, deliberately not collapsed into one (review response). An ABSENT speciality
 * is the expected state today — the field is not on `UserData` and the backend returns it empty for
 * staff accounts. An UNRECOGNISED non-empty value is a DATA problem: something populated the field
 * with a discipline this app has no framework for. Both ask the same question, but the second must
 * not silently present as the first, or a mis-seeded speciality is invisible forever.
 */
export type AlliedPlanPickReason = 'speciality-absent' | 'speciality-unrecognised';

/** What the step should render for a given user. */
export type AlliedPlanResolution =
  | { kind: 'resolved'; framework: AlliedPlanFramework; plan: AlliedPlan }
  | { kind: 'needs-framework'; reason: AlliedPlanPickReason; spine: AlliedPlanSpineSection };

/**
 * Build the plan for a chosen framework. The ONLY constructor of an `AlliedPlan`, and it always
 * supplies the spine — which is why the spine cannot go missing.
 */
export function alliedPlanFor(framework: AlliedPlanFramework): AlliedPlan {
  return { spine: PLAN_CORE, frameworks: [FRAMEWORK_SECTIONS[framework]] };
}

/**
 * Resolve what the plan step should show.
 *
 * NO FAIL-OPEN "SHOW ALL THREE" (review response, PROD-2040 — this replaces the previous
 * behaviour). `speciality` is unresolved for EVERY current user, so showing all three frameworks
 * was not a graceful fallback for a minority case: it was the permanent state dressed as an edge
 * case, and it would have quietly become the de facto UX until the backend catches up. Asking the
 * clinician once makes the unresolved case visible and intentional instead.
 *
 * Takes a plain `string` because that is what the wire hands over — an arbitrary value, not a
 * member of `AlliedSpeciality`. Whitespace-only is treated as absent.
 */
export function resolveAlliedPlan(speciality?: string | null): AlliedPlanResolution {
  const trimmed = typeof speciality === 'string' ? speciality.trim() : '';
  if (trimmed === '') {
    return { kind: 'needs-framework', reason: 'speciality-absent', spine: PLAN_CORE };
  }
  const framework = (FRAMEWORK_FOR_SPECIALITY as Record<string, AlliedPlanFramework | undefined>)[
    trimmed
  ];
  if (framework === undefined) {
    return { kind: 'needs-framework', reason: 'speciality-unrecognised', spine: PLAN_CORE };
  }
  return { kind: 'resolved', framework, plan: alliedPlanFor(framework) };
}

/** Every section of a plan, spine first — what the view iterates. */
export function planSections(plan: AlliedPlan): AlliedPlanSection[] {
  return [plan.spine, ...plan.frameworks];
}

/** Every framework section the registry can yield — for the order-affordance sweep. */
export function allFrameworkSections(): AlliedPlanFrameworkSection[] {
  return FRAMEWORK_CHOICES.map((c) => FRAMEWORK_SECTIONS[c.framework]);
}

/**
 * Does any field in these sections read as an ORDER or PRESCRIBE affordance?
 *
 * The mechanical form of "allied health does not prescribe". Matches on the field id, the label
 * AND the hint, so neither a rename nor a new field can quietly introduce one. Hyphens in ids are
 * normalised to spaces first so `\b` anchors land on the id's words (`plan-order` → `plan order`).
 */
export function hasOrderAffordance(sections: readonly AlliedPlanSectionBase[]): boolean {
  return sections.some((section) =>
    section.fields.some((field) => {
      const haystack = `${field.id} ${field.label} ${field.hint ?? ''}`.replace(/[-_]/g, ' ');
      return ORDER_AFFORDANCE_PATTERNS.some((pattern) => pattern.test(haystack));
    }),
  );
}
